import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Ridgeline } from "@/components/common/Ridgeline";
import { EDMONTON_COMMUNITIES } from "@/data/communities";

const HOME_TYPES = ["Condo", "Townhome", "Suite", "House"];

const NOT_SURE = "Not sure yet — scout me";

/**
 * First Tracks waitlist form.
 * POSTs to /api/first-tracks. All fields are optional except name + email.
 */
export const FirstTracksForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    moving_from: "",
    target_move_date: "",
    home_types: [],
    preferred_areas: [],
    budget: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleInArray = (key, value) => {
    setForm((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(value)
        ? arr.filter((x) => x !== value)
        : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError("Please share your name and email so a Scout can say hello.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/first-tracks`,
        form,
      );
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden p-10 text-center"
        style={{
          borderRadius: 24,
          backgroundColor: "#F8F5EF",
          boxShadow: "0 30px 60px -30px rgba(20,35,27,0.4)",
        }}
        data-testid="first-tracks-success"
      >
        <div className="mx-auto w-[min(460px,90%)]">
          <Ridgeline stroke="#C89A4E" height={100} />
        </div>
        <p
          className="label-caps mt-6 text-[11px]"
          style={{ color: "#C89A4E" }}
        >
          You&rsquo;re on the list
        </p>
        <h3
          className="font-display mt-4"
          style={{
            fontSize: "clamp(28px,3.6vw,44px)",
            color: "#201E1A",
            lineHeight: 1.05,
          }}
        >
          Fresh tracks coming your way &mdash; and a Scout will say hello
          within a day.
        </h3>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="relative p-8 md:p-10"
      style={{
        borderRadius: 24,
        backgroundColor: "#F8F5EF",
        boxShadow: "0 30px 60px -30px rgba(20,35,27,0.4)",
      }}
      data-testid="first-tracks-form"
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Field label="Your name" required>
          <input
            type="text"
            value={form.name}
            onChange={upd("name")}
            required
            data-testid="ft-name"
            className="input"
          />
        </Field>
        <Field label="Email" required>
          <input
            type="email"
            value={form.email}
            onChange={upd("email")}
            required
            data-testid="ft-email"
            className="input"
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={form.phone}
            onChange={upd("phone")}
            data-testid="ft-phone"
            className="input"
          />
        </Field>
        <Field label="Moving from (city)">
          <input
            type="text"
            value={form.moving_from}
            onChange={upd("moving_from")}
            placeholder="e.g. Vancouver"
            data-testid="ft-city"
            className="input"
          />
        </Field>
        <Field label="Target move date">
          <input
            type="date"
            value={form.target_move_date}
            onChange={upd("target_move_date")}
            data-testid="ft-date"
            className="input"
          />
        </Field>
        <Field label="Budget range (per month)">
          <input
            type="text"
            value={form.budget}
            onChange={upd("budget")}
            placeholder="e.g. $1,600–$2,200"
            data-testid="ft-budget"
            className="input"
          />
        </Field>
      </div>

      {/* home types */}
      <div className="mt-8">
        <p className="label-caps text-[10px]" style={{ color: "rgba(32,30,26,0.55)" }}>
          I&rsquo;m looking for
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {HOME_TYPES.map((t) => (
            <ChipToggle
              key={t}
              active={form.home_types.includes(t)}
              onClick={() => toggleInArray("home_types", t)}
              testId={`ft-home-${t.toLowerCase()}`}
            >
              {t}
            </ChipToggle>
          ))}
        </div>
      </div>

      {/* preferred areas */}
      <div className="mt-8">
        <p className="label-caps text-[10px]" style={{ color: "rgba(32,30,26,0.55)" }}>
          Preferred areas
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {EDMONTON_COMMUNITIES.map((c) => (
            <ChipToggle
              key={c.slug}
              active={form.preferred_areas.includes(c.slug)}
              onClick={() => toggleInArray("preferred_areas", c.slug)}
              testId={`ft-area-${c.slug}`}
            >
              {c.name}
            </ChipToggle>
          ))}
          <ChipToggle
            active={form.preferred_areas.includes(NOT_SURE)}
            onClick={() => toggleInArray("preferred_areas", NOT_SURE)}
            testId="ft-area-notsure"
            variant="dashed"
          >
            {NOT_SURE}
          </ChipToggle>
        </div>
      </div>

      {/* notes */}
      <div className="mt-8">
        <p className="label-caps text-[10px]" style={{ color: "rgba(32,30,26,0.55)" }}>
          Anything we should know?
        </p>
        <textarea
          rows={3}
          value={form.notes}
          onChange={upd("notes")}
          placeholder="Kids, pets, hospital shifts, remote work…"
          className="input mt-2 resize-none"
          data-testid="ft-notes"
        />
      </div>

      {error && (
        <p className="mt-6 text-[13px]" style={{ color: "#8B1A0F" }} data-testid="ft-error">
          {error}
        </p>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="pill pill-gold text-[15px] disabled:opacity-60"
          data-testid="ft-submit"
        >
          {submitting ? "Sending…" : "Get First Tracks access"}
        </button>
        <p className="text-[12px]" style={{ color: "rgba(32,30,26,0.55)" }}>
          One email a week, no spam. Unsubscribe anytime.
        </p>
      </div>

      {/* input styles scoped inline to avoid a global stylesheet edit */}
      <style>{`
        .input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(32,30,26,0.22);
          padding: 0.55rem 0;
          font-size: 15px;
          color: #201E1A;
          outline: none;
          transition: border-color 0.3s;
        }
        .input:focus { border-color: #24382E; }
      `}</style>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span
      className="label-caps text-[10px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {label}
      {required && <span style={{ color: "#C89A4E" }}> *</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const ChipToggle = ({ active, onClick, testId, variant, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className="rounded-full px-3.5 py-1.5 text-[12px] transition-all"
    style={{
      border: `${variant === "dashed" ? "1px dashed" : "1px solid"} ${
        active ? "#24382E" : "rgba(32,30,26,0.2)"
      }`,
      backgroundColor: active ? "#24382E" : "transparent",
      color: active ? "#F8F5EF" : "#24382E",
    }}
  >
    {children}
  </button>
);

export default FirstTracksForm;
